import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../services/firebase';

interface CertificateTemplate {
  id: string;
  name: string;
  description: string;
  templateUrl: string;
  templateBase64?: string;
  storagePath?: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplateManagerProps {
  domain: string;
}

const TemplateManager: React.FC<TemplateManagerProps> = ({ domain }) => {
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<CertificateTemplate | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (domain) {
      loadTemplates();
    }
  }, [domain]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const templatesRef = collection(db, `school-data/${domain}/certificate-templates`);
      const snapshot = await getDocs(templatesRef);

      const templateList: CertificateTemplate[] = [];
      snapshot.forEach((doc) => {
        templateList.push({ id: doc.id, ...doc.data() } as CertificateTemplate);
      });

      templateList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setTemplates(templateList);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image (PNG, JPG) file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const openAddModal = () => {
    setEditingTemplate(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (template: CertificateTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setPreviewUrl(template.templateUrl);
    setSelectedFile(null);
    setShowModal(true);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please provide a template name');
      return;
    }

    if (!editingTemplate && !selectedFile) {
      alert('Please select a template file');
      return;
    }

    setUploading(true);
    try {
      if (editingTemplate) {
        // Update existing template
        let updateData: Record<string, string> = {
          name: templateName.trim(),
          description: templateDescription.trim(),
          updatedAt: new Date().toISOString(),
        };

        // If new file selected, upload it
        if (selectedFile) {
          const fileName = `${Date.now()}_${selectedFile.name}`;
          const storageRef = ref(storage, `${domain}/certificate-templates/${fileName}`);
          await uploadBytes(storageRef, selectedFile);
          const downloadUrl = await getDownloadURL(storageRef);

          // Convert to base64
          const templateBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(selectedFile);
          });

          updateData = {
            ...updateData,
            templateUrl: downloadUrl,
            templateBase64: templateBase64,
            storagePath: `${domain}/certificate-templates/${fileName}`,
          };

          // Delete old file from storage
          if (editingTemplate.storagePath) {
            try {
              const oldStorageRef = ref(storage, editingTemplate.storagePath);
              await deleteObject(oldStorageRef);
            } catch {
              console.log('Old file may not exist');
            }
          }
        }

        await updateDoc(doc(db, `school-data/${domain}/certificate-templates`, editingTemplate.id), updateData);
        alert('Template updated successfully!');
      } else {
        // Add new template
        const fileName = `${Date.now()}_${selectedFile!.name}`;
        const storageRef = ref(storage, `${domain}/certificate-templates/${fileName}`);
        await uploadBytes(storageRef, selectedFile!);
        const downloadUrl = await getDownloadURL(storageRef);

        // Convert to base64
        const templateBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile!);
        });

        const templatesRef = collection(db, `school-data/${domain}/certificate-templates`);
        await addDoc(templatesRef, {
          name: templateName.trim(),
          description: templateDescription.trim(),
          templateUrl: downloadUrl,
          templateBase64: templateBase64,
          storagePath: `${domain}/certificate-templates/${fileName}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        alert('Template uploaded successfully!');
      }

      resetForm();
      setShowModal(false);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteTemplate = async (template: CertificateTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, `school-data/${domain}/certificate-templates`, template.id));

      if (template.storagePath) {
        try {
          const storageRef = ref(storage, template.storagePath);
          await deleteObject(storageRef);
        } catch {
          console.log('Storage file may not exist or already deleted');
        }
      }

      alert('Template deleted successfully!');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setTemplateName('');
    setTemplateDescription('');
    setEditingTemplate(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">
            <i className="bi bi-file-earmark-image me-2 text-primary-custom"></i>
            Certificate Templates
          </h5>
          <small className="text-muted">Upload and manage certificate templates</small>
        </div>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="bi bi-plus-circle me-2"></i>
          Upload New Template
        </button>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-3">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-file-earmark-image fs-1 text-muted d-block mb-3"></i>
            <h5 className="text-muted">No Templates Found</h5>
            <p className="text-muted mb-4">Upload your first certificate template to get started</p>
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="bi bi-upload me-2"></i>
              Upload Template
            </button>
          </div>
        ) : (
          <div className="row g-4">
            {templates.map((template) => (
              <div key={template.id} className="col-md-4">
                <div className="card h-100 border shadow-sm">
                  <div className="card-img-top bg-light d-flex align-items-center justify-content-center" style={{ height: '180px' }}>
                    {template.templateUrl ? (
                      <img
                        src={template.templateUrl}
                        alt={template.name}
                        className="img-fluid"
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerHTML = '<i class="bi bi-file-earmark-image fs-1 text-muted"></i>';
                        }}
                      />
                    ) : (
                      <i className="bi bi-file-earmark-image fs-1 text-muted"></i>
                    )}
                  </div>
                  <div className="card-body">
                    <h6 className="card-title">{template.name}</h6>
                    <p className="card-text text-muted small">
                      {template.description || 'No description'}
                    </p>
                  </div>
                  <div className="card-footer bg-white border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {new Date(template.createdAt).toLocaleDateString()}
                      </small>
                      <div className="btn-group btn-group-sm">
                        <a
                          href={template.templateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary"
                          title="View Template"
                        >
                          <i className="bi bi-eye"></i>
                        </a>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => openEditModal(template)}
                          title="Edit Template"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => deleteTemplate(template)}
                          title="Delete Template"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className={`bi ${editingTemplate ? 'bi-pencil' : 'bi-upload'} me-2`}></i>
                  {editingTemplate ? 'Edit Template' : 'Upload Certificate Template'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  disabled={uploading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Template Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Merit Certificate, Participation Certificate"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Brief description of the certificate..."
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    disabled={uploading}
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Template Image {!editingTemplate && <span className="text-danger">*</span>}
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                  <small className="text-muted">
                    Supported: PNG, JPG (max 5MB)
                    {editingTemplate && ' - Leave empty to keep current image'}
                  </small>
                </div>

                {/* Preview */}
                {previewUrl && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Preview</label>
                    <div
                      className="border rounded bg-light d-flex align-items-center justify-content-center p-2"
                      style={{ maxHeight: '200px' }}
                    >
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="img-fluid"
                        style={{ maxHeight: '180px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveTemplate}
                  disabled={uploading || !templateName.trim() || (!editingTemplate && !selectedFile)}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {editingTemplate ? 'Updating...' : 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <i className={`bi ${editingTemplate ? 'bi-check-lg' : 'bi-cloud-upload'} me-2`}></i>
                      {editingTemplate ? 'Update Template' : 'Upload Template'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;
